import { isValidElement, useLayoutEffect, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent, type ReactElement, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Color,
  DOCUMENT_VIEW_PARAM,
  componentBreakpoints,
  fill, Size,
  isTermPrintedKind,
  t,
  type IconMeaning,
  type PrintDocument,
  type PrintSheetHtml,
  type PrintSheetLayout,
  type StatusColorGroup,
  type StringKey,
} from '@bms2/shared';
import { AlertBanner, Button, IconButton, SegmentedTab, SplitAsButtonsContext } from '../../components/controls';
import { useBelowWidth, usePhoneWidth } from '../../components/usePhoneWidth';
import { rememberTileFolded, tileFolded } from '../../components/composites/Tile';
import { Icon } from '../../components/Icon';
import { Money, MoneyText, drawnMoney } from '../../components/Money';
import { DOCUMENT_VIEW_KEY, readPreference, writePreference } from '../../shell/devicePreference';
import { PageModule } from '../../shell/PageModule';
import { useStatusResolver } from '../../shell/StatusResolver';
import { openSheet } from './ViewPdfLink';
import { DocumentSheetFrame } from './DocumentSheetFrame';
import { DocumentTermsModal } from './DocumentTermsModal';
import { PageFacts, type ScreenFact } from './PageFacts';
import { BannerAvatarSlot } from './RecordBannerHead';
import { useListPaneOpen } from './listPaneOpen';
import { PRINT_TEMPLATES_ROUTE } from '../settings/api';
import styles from './DocumentPage.module.css';
import railStyles from './DocumentRail.module.css';

/**
 * 🔒 One accounting document view page, fed a descriptor per kind (decision
 * 0612; piece 22). The product owner asked for two views of one document and for
 * the sheet first: *"accounting document 2 views and pdf preview on the when
 * clicking the list item"*, and *"The accounting documents should be using a
 * standard format function for UI layout that is just being fed different data
 * sets per the spec. I dont feel this app is currently doing this."*
 *
 * 🔨 The per-kind difference is data and never a second page. Which facts the
 * Details view carries, which address blocks it draws, which columns the line
 * table has and which panels stand beneath it all arrive as props; the layout of
 * both views is here, once, for every kind.
 *
 * 🔒 **PDF** draws the sheet as the print layout draws it, with an action bar
 * above it and a corner status ribbon over it; **Details** draws the parties, the
 * lines and the panels. The choice is a segmented control at the top right of the
 * pane, remembered per browser, and it opens on the sheet.
 *
 * 🔨 Edit mode is not here. It is already one shared page (`DocumentEditPage`),
 * and the URL carries the mode (decision 0409).
 */
export const documentViewPageDocumentedStates = ['pdf', 'details', 'noSheet'] as const;

/** Which of the two views is being read. */
export type DocumentView = 'pdf' | 'details';

/**
 * 🔒 Where the choice is remembered: per browser, never on the server (decision
 * 0612). It is a reading preference on the screen in front of somebody and not a
 * fact about the document, and a browser that refuses to store reads as the
 * sheet, which is what opening a row from a list shows.
 */
export { DOCUMENT_VIEW_KEY };

export function readDocumentView(): DocumentView {
  return readPreference<DocumentView>(DOCUMENT_VIEW_KEY, (raw) => (raw === 'details' ? 'details' : 'pdf'), 'pdf');
}

export function rememberDocumentView(view: DocumentView): void {
  writePreference(DOCUMENT_VIEW_KEY, view);
}

/**
 * One middle cell that carries attributes of its own, such as the Quote line's
 * tax category or its account. A cell that carries none is written as its text.
 */
export interface DocumentLineCell {
  text: string;
  /** 🔒 The text is a formatted money amount; only then is it scanned for a negative figure (decision 2155). */
  money?: boolean;
  attributes?: Record<string, string>;
  /**
   * 🔒 A control the cell carries instead of its text (T182). A stock count is
   * read and written on the same table — the counted quantity and the reason it
   * differs are typed into the row — so a cell holds a field where the kind's own
   * state allows one, and its text where it does not.
   */
  node?: ReactNode;
  /**
   * 🔒 A second, smaller line beneath the cell's figure, as the sheet stacks a
   * sales line's list price beneath its unit price (decisions 1063, 10040).
   */
  sub?: ReactNode;
  /** Attributes the second line carries, such as the list price in cents a suite reads. */
  subAttributes?: Record<string, string>;
}

/**
 * 🔒 A sales line's unit price as the sheet draws it (decisions 1063, 10040): the
 * figure, and beneath it *List* and the list price where the line carries one,
 * which is the figure frozen when the document was sent.
 */
export function listPricedCell(price: ReactNode, listPriceCents: number | null | undefined, currencyCode: string): DocumentLineCell {
  if (listPriceCents === null || listPriceCents === undefined) return { text: '', node: price };
  return { text: '', node: price, sub: <>{t('document.print.listPrice')} <Money cents={listPriceCents} currencyCode={currencyCode} /></>, subAttributes: { 'data-document-list-price': String(listPriceCents) } };
}

/** One line of a document as the read-only table draws it: a leading cell, the middle cells in order, and the money. */
export interface DocumentLineRow {
  id: string;
  /** The row's first cell: the identifying text the kind composes, such as `SKU · description`. */
  label: string;
  /** 🔒 Where the label opens, where the row names a record that has a page, such as a payment's bill (T3754); plain text where absent. */
  route?: string | null;
  /** A second line under the label, where the kind has one to draw: a sentence, or the kind's own marks. */
  note?: ReactNode;
  /** Attributes the note carries, such as the sales order line's own invoiced state. */
  noteAttributes?: Record<string, string>;
  /**
   * The middle cells, in the order the columns name them: text already written,
   * or a node — a unit price is the Money component over the cents (decision 0717).
   */
  cells: readonly (string | null | DocumentLineCell | ReactElement)[];
  /** 🔒 The row's money, the Money component over the cents (decision 0717); absent on a quantities-only kind. */
  amount?: ReactNode;
  /** Extra attributes a suite reads this row by, such as the line's own tax or account. */
  attributes?: Record<string, string>;
  /** A control on the row itself, such as the payment allocation's own Dissociate. */
  action?: ReactNode;
  /** 🔒 The sheet's taxable mark, a small T beside the amount of a line charged tax (decisions 1062, 10040). */
  taxable?: boolean;
}

/** What one kind's line table looks like: the block's heading, the row attribute and the middle column headings. */
export interface DocumentLinesDescriptor {
  /** The block's own heading. */
  title: string;
  /** The attribute each row carries its id in, such as `data-invoice-line`. */
  rowAttribute: string;
  /** The attribute the block carries its own count in, such as `data-invoice-lines`. */
  blockAttribute?: string;
  /** What to say where the document has no lines at all. */
  empty?: string | null;
  /**
   * 🔒 One sentence under the block's heading that says how its fields are read,
   * where the kind needs one: a count's counted quantity is the count on the
   * shelf, not the change (decision 1142).
   */
  hint?: string | null;
  /**
   * 🔒 The column headings, where the kind names them (T182). A money document's
   * line is self-describing — a description, a quantity, a unit price and an
   * amount — and a stock document's is not: Expected, Counted and Variance are
   * three numbers that read identically without a heading over each.
   */
  columns?: readonly string[];
  /**
   * 🔒 Whether each row carries an amount at its right, true where the kind says
   * nothing (T182). A stock transfer and a stock count move quantities and no
   * money, so neither draws a money column and neither foots to a total.
   */
  money?: boolean;
  rows: readonly DocumentLineRow[];
  /** The totals drawn under the rows, in order; the last one reads strongest. */
  totals?: readonly { label: ReactNode; value: ReactNode; attributes?: Record<string, string>; /** A row whose label is not plain text names its own key (decision 0738). */ key?: string }[];
}

export interface DocumentViewPageProps {
  /** The document's own type, as the projection and the registries name it. */
  documentType: string;
  icon: IconMeaning;
  /** The number, which is the banner's title. */
  title: string;
  subtitle?: string | null;
  /**
   * 🔒 The document's identifying facts, drawn at the top of the Details view and
   * nowhere in the banner (T170). The product owner, 2026-09-22: *"do not put the
   * details in the title bar like Account or against invoice it is in the details
   * or pdf view."* The sheet carries its own information table, so the two views
   * each say what the document is against.
   */
  information?: readonly ScreenFact[];
  /** The status pills, as each kind resolves them. */
  status?: ReactNode;
  /** The small pills under the title, such as the purchase order's own fulfilment method (decision 0335). */
  kinds?: ReactNode;
  /** The page's own view controls, such as a Help button. */
  controls?: ReactNode;
  /**
   * 🔒 The toolbar row beneath the title bar (decision 1440). Nothing given, no row
   * is drawn.
   */
  toolbar?: ReactNode;
  /** 🔒 The gear to the settings that govern the kind, at the title bar's right end (decisions 2805, 2815; T1614, T2056). */
  gear?: ReactNode;
  /** The record's own tabs, at the left of the same bar the two views are chosen on. */
  tabs?: ReactNode;
  /**
   * 🔒 The kind's own footer controls, which the one action bar lays out (T168).
   * No wrapper and no stylesheet of the page's own: the bar is built once in this
   * file for every accounting document.
   */
  footer?: ReactNode;
  /** The attribute the footer has always carried, such as `data-bill-actions`, spelled as the page has always spelled it. */
  footerAttributes?: Record<string, string>;
  /**
   * 🔒 How many of the footer's first acts stand on a phone before More (decision 3961). The default two is every
   * document's (decision 3052); the Service Report hands three, its clock, Add Note and Complete the Visit.
   */
  footerLeads?: number;
  /**
   * 🔒 Reopening the document's form, at the right of the page footer (T168,
   * amending decision 0612). The product owner: *"Edit in the footer at the
   * right"*. It is drawn in one place and one only.
   */
  onEdit?: () => void;
  /**
   * A kind whose Edit is not one word — the quote's reads as the revision it will
   * raise — hands the whole control instead, and it stands where every other
   * kind's Edit stands.
   */
  editAction?: ReactNode;
  /** The banner's pencil, which corrects a document in place rather than reopening its form (decision 0409). */
  onCorrect?: () => void;
  /** Attributes the page's own element carries, which the suites read it by. */
  pageAttributes?: Record<string, string>;
  /** Banners and sentences drawn above both views. */
  notices?: ReactNode;
  /**
   * 🔒 The What's next banner (decision 1732), drawn under the notices on both
   * views. 🔨 A viewer the write gate refuses is offered no move, so it is not drawn.
   */
  whatsNext?: ReactNode;
  /**
   * The projection the Details view's parties block reads; null where the answer
   * carries none. 🔨 It is not what the sheet is drawn from any more (T169): the
   * sheet is the printer's own HTML, and a second drawing of this projection is
   * what the screen and the paper used to disagree through.
   */
  print?: PrintDocument | null;
  /**
   * 🔒 The sheet the PDF view draws, as the printer's own HTML (T169). It arrives
   * in the page's own answer, so the sheet is on screen in the same paint as the
   * page: no spinner, no second read.
   */
  sheet?: PrintSheetHtml | null;
  /**
   * The sheet's page size, which is all the frame reads (T350). A document hands its whole layout; a signed Service
   * Report hands its frozen PDF's page alone (decision 3962).
   */
  layout?: { page: Pick<PrintSheetLayout['page'], 'widthInches' | 'heightInches'> } | null;
  /**
   * 🔒 The status word the PDF view's band reads, in capitals, across the sheet's
   * top-left corner in the status's own colour (decisions 0612, 1457). It is a
   * screen affordance and never printed.
   */
  ribbon?: string | null;
  /**
   * 🔒 The stored status value the band's colour resolves by, where the word drawn
   * is the status's label rather than its value (found in review, 2026-09-30).
   * 💰 The vocabulary is keyed on the value, so "Partly Received" or "Delivery
   * Failed" matched no row and drew grey; a page drawing a label passes the value
   * here, and a page drawing the value itself leaves it out.
   */
  ribbonStatus?: string | null;
  /**
   * 🔒 The band's colour group where the page draws a pill no status row carries, a held Draft's Pending Approval
   * (T1195; decision 1861); it wins over the resolved row, and the words drawn are the ribbon's own.
   */
  ribbonGroup?: StatusColorGroup | null;
  /** The sheet's own path, relative to the API root; null where the kind prints none. */
  pdfPath?: string | null;
  /**
   * 🔒 Whether this document offers a sheet at all in its present state (decision 3960). An open Service Report has
   * none until its lead signs, so it opens on Details and draws no Details | PDF choice; every other kind leaves it
   * out and the bar stands as it always has.
   */
  sheetOffered?: boolean;
  /**
   * 🔒 A control the kind stands above the sheet on the PDF view and nowhere on
   * Details, such as the RFQ's vendor picker, which chooses whom the sheet is
   * drawn for (decision 1513).
   */
  sheetHeader?: ReactNode;
  /** Where the close cross goes: the kind's own list. */
  listRoute?: string | null;
  /** The lines, drawn once for every kind by the table below. */
  lines?: DocumentLinesDescriptor | null;
  /**
   * 🔒 The parties a kind names in place of the projection's recipient and
   * deliver-to (T442; decision 1120). The RFQ hands one per vendor asked; every
   * other kind leaves it out and the block draws the projection's two.
   */
  parties?: readonly DocumentParty[] | null;
  /** The one heading over the row list a block of more than three parties draws. */
  partiesHeading?: string;
  /** Everything else the Details view draws: the parties block is built here, the panels arrive from the kind. */
  details?: ReactNode;
  /**
   * 🔒 The document's Internal Notes, which the PDF view draws as a collapsible
   * rail at the right of the sheet, and after it in the page's flow on a phone
   * (T587, T2189; decisions 1403, 3050).
   * The kind hands the same node it places in its Details view: only one view is
   * mounted at a time, so the notes are read once whichever is shown.
   *
   * 🔒 A kind whose page read withheld the notes passes null, and no rail mounts
   * at any width (found in review, 2026-09-30): a folded rail draws its strip
   * without its body, so an outside login on a phone saw an Internal Notes strip.
   */
  notes?: ReactNode;
  /**
   * 🔒 The document's Files block, its Account Journal and its History, each the
   * node the kind places in its Details view, handed again so the PDF view's rail
   * draws each as a tab beside Notes (T1070; decision 1662, amending 1403). Only
   * one view is mounted, so each is read once. A kind leaves out what it has none
   * of, and that tab is not drawn.
   */
  files?: ReactNode;
  journal?: ReactNode;
  history?: ReactNode;
  /** The modals the kind opens, mounted outside both views so a view change does not close one. */
  children?: ReactNode;
  apiBaseUrl?: string;
  /**
   * 🔒 The document's own Terms (T125, decision 0684): its id, and what the page
   * reads again after a save. On the seven printed kinds the footer carries a
   * Terms button that opens the window listing the clauses in force.
   */
  terms?: { documentId: string; onSaved: () => void } | null;
  /**
   * 🔒 Why this viewer writes nothing on this document, as the server's own
   * document write gate words it (decision 0687), or null where they may. The
   * footer's controls, Edit and the banner's pencil are withheld and the reason
   * stands in their place; the server refuses the write either way.
   */
  writeRefusal?: string | null;
  /**
   * 🔒 A move that is the viewer's own and writes nothing on the document, so the
   * write refusal never withholds it: a portal login's Pay this invoice
   * (decision 1650). It stands in the footer whether or not the viewer may write.
   */
  readerAction?: ReactNode;
}

/**
 * 🔒 The read-only line table, written once (decision 0612). 💰 It was written
 * three times over three near-identical stylesheets before this, so the Quote's
 * rows, the Invoice's and the Bill's drifted into three shapes of one thing.
 *
 * 🔨 It is not the Item Table (piece 4). That construction is an editor: its
 * read-only mode still wants an item search, a change handler and the editor's
 * own column set, which is not the set a sheet draws.
 */
export function DocumentLines({ lines }: { lines: DocumentLinesDescriptor }) {
  /**
   * 🔒 The grid is given the tracks the widest row of THIS kind needs, counted
   * here and handed to the stylesheet as a number (found in review, 2026-09-21).
   * 💰 The stylesheet named four tracks, which is a leading cell, two middle cells
   * and the money: the Quote draws five middle cells, so its money and two of its
   * cells landed in implicit columns the media rule never sized.
   *
   * 🔨 Every row draws the same number of middle cells, a short row's spare ones
   * empty, so the money stands in one column down the whole table. A row of its own
   * length would put the Quote's amount under another row's tax category.
   */
  /* 🔨 One track at least: `repeat(0, …)` is not a grid template, and a kind whose rows carry no middle cell at all would lose the whole rule. */
  const middles = lines.rows.reduce((most, row) => Math.max(most, row.cells.length), Math.max((lines.columns?.length ?? 1) - 1, 1));
  const anyAction = lines.rows.some((row) => row.action !== undefined);
  const money = lines.money !== false;
  const anyTaxable = money && lines.rows.some((row) => row.taxable === true);
  const trackStyle = { '--document-row-middles': middles } as CSSProperties;
  const rowClass = [styles.row, anyAction ? styles.rowWithAction : '', money ? '' : styles.rowNoAmount].filter(Boolean).join(' ');
  return (
    <>
      {lines.rows.length === 0 && lines.empty ? <p className={styles.muted}>{lines.empty}</p> : null}
      <ul className={styles.lineList} style={trackStyle} data-document-line-middles={middles}>
        {/* 🔒 The heading row a quantities table names, on the same grid as its rows (T182). */}
        {lines.columns ? (
          <li className={`${rowClass} ${styles.rowHead}`} data-document-line-head={String(lines.columns.length)}>
            {lines.columns.map((column, index) => (
              <span key={column} className={index === 0 ? styles.lineMain : styles.rowMiddle}>{column}</span>
            ))}
          </li>
        ) : null}
        {lines.rows.map((row) => (
          <li
            key={row.id}
            className={rowClass}
            {...{ [lines.rowAttribute]: row.id }}
            {...(row.attributes ?? {})}
          >
            <span className={styles.lineMain}>
              <span className={styles.rowName}>{row.route ? <Link className={styles.rowLink} to={row.route}>{row.label}</Link> : row.label}</span>
              {row.note ? <span className={styles.rowNote} {...(row.noteAttributes ?? {})}>{row.note}</span> : null}
            </span>
            {Array.from({ length: middles }, (_ignored, index) => {
              const cell = row.cells[index] ?? null;
              const held: DocumentLineCell = cell === null ? { text: '' } : typeof cell === 'string' ? { text: cell } : isValidElement(cell) ? { text: '', node: cell } : (cell as DocumentLineCell);
              return (
                <span
                  key={index}
                  className={styles.rowMiddle}
                  data-document-cell={index}
                  /* 🔒 Below the breakpoint the row stacks, so a named column reads its own name before its value (T182). */
                  {...(lines.columns?.[index + 1] ? { 'data-document-column': lines.columns[index + 1] } : {})}
                  {...(held.attributes ?? {})}
                >
                  {'node' in held && held.node !== undefined ? held.node : held.text === '' ? '' : held.money ? <MoneyText text={held.text} /> : held.text}
                  {held.sub === undefined ? null : <span className={styles.cellSub} {...(held.subAttributes ?? {})}>{held.sub}</span>}
                </span>
              );
            })}
            {money ? (
              <span className={styles.rowAmount} data-document-amount="true">
                {drawnMoney(row.amount ?? '')}
                {/* 🔒 The sheet's T beside a taxed line's amount, with the legend under the table saying what it means (decisions 1062, 10040). */}
                {anyTaxable ? <span className={row.taxable ? styles.taxMark : `${styles.taxMark} ${styles.taxMarkBlank}`} {...(row.taxable ? { 'data-document-taxable': 'true' } : { 'aria-hidden': true })}>{t('document.print.taxableMark')}</span> : null}
              </span>
            ) : null}
            {row.action === undefined ? null : <span className={styles.rowAction}>{row.action}</span>}
          </li>
        ))}
      </ul>
      {anyTaxable ? <p className={styles.taxLegend} data-document-tax-legend="true">{t('document.print.taxableLegend')}</p> : null}
      {(lines.totals ?? []).map((total, index) => (
        <p
          key={total.key ?? String(total.label)}
          className={index === (lines.totals ?? []).length - 1 ? styles.totalStrong : styles.total}
          {...(total.attributes ?? {})}
        >
          {total.label} {drawnMoney(total.value)}
        </p>
      ))}
    </>
  );
}

/**
 * 🔒 The bar every accounting document carries above its two views (decisions
 * 0612, 0626): the choice at the top right of the pane with the record's own tabs
 * at the left of it, the download chevron inside the pill and a cross back to the
 * list. 🔒 On the PDF view it stands at the right end of the zoom row, so the sheet
 * has one control row above it; on Details it stands above the facts (T335).
 *
 * 🔨 It is a component of its own because the Work Order is drawn by the record
 * page's tile canvas rather than by the page below, and one bar drawn twice is
 * two bars to keep in step.
 *
 * 🔨 The document's other actions stay in the page's own footer rather than being
 * copied into a menu here: two controls for one act is two things to keep in
 * step. 🔨 Print is inside the viewer, where the bytes are drawn — printing a
 * sheet nobody fetched would be a second fetch (decision 0563).
 */
export function DocumentViewControls({ documentType, view, onChoose, tabs, pdfPath = null, listRoute = null, apiBaseUrl = '', part = 'bar', choiceOffered = true }: {
  /**
   * 🔒 False where the document has no sheet to choose (T2396): the Details | PDF choice and the download are not
   * drawn, and the tabs and the cross back to the list stay.
   */
  choiceOffered?: boolean;
  documentType: string;
  /**
   * 🔒 Which part is drawn (T2190; decision 3051). `bar` is the whole bar; on a phone's PDF view the row is one,
   * so the switch stands at its head as `switch` and the download at its right as `download`, and the cross is not
   * drawn: the switch is how a person leaves the sheet there.
   */
  part?: 'bar' | 'switch' | 'download';
  /** The document's own name, its number, for the file a refused tab saves instead (T318). */
  documentName?: string;
  view: DocumentView;
  onChoose: (view: DocumentView) => void;
  tabs?: ReactNode;
  pdfPath?: string | null;
  listRoute?: string | null;
  apiBaseUrl?: string;
}) {
  const navigate = useNavigate();
  /**
   * 🔒 The chevron opens the PDF in a browser tab, whose own viewer offers the save
   * (T318). 🔨 It is opened the way View PDF is (T541): the tab reserved at the press and
   * the bytes read through the one sheet path, which carries the View-as headers.
   */
  const download = () => {
    if (pdfPath === null) return;
    void openSheet(`${apiBaseUrl}${pdfPath}`);
  };
  const downloadButton = pdfPath === null ? null : (
    <IconButton meaning="download" label={t('document.view.openPdf')} size={Size.Small} name={`document-download-${documentType}`} onClick={download} />
  );
  const choice = (
    <SegmentedTab
      name={`document-view-${documentType}`}
      legend={t('document.view.legend')}
      segments={[
        { value: 'details', label: t('document.view.details') },
        { value: 'pdf', label: t('document.view.pdf') },
      ]}
      value={view}
      onChange={(next) => onChoose(next === 'details' ? 'details' : 'pdf')}
    />
  );
  if (part === 'download') return downloadButton;
  if (part === 'switch') {
    return (
      <div className={styles.viewSwitch} data-document-view={view} data-document-actions={documentType} data-document-view-part="switch">
        {tabs}
        {choice}
      </div>
    );
  }
  return (
    <div className={styles.viewBar} {...(choiceOffered ? { 'data-document-view': view } : { 'data-document-view-none': 'true' })} data-document-actions={documentType}>
      <div className={styles.tabs}>{tabs}</div>
      {/*
        * 🔒 One control, not three (T168). The product owner, 2026-09-22: *"Do not
        * like the edit button new placement at all or the pdf Download buttons it is
        * waste of space."* The chevron sits inside the left end of the same pill the
        * two views are chosen on, and the row the two links and Edit stood on is
        * gone: the download is the sheet the pane is already showing, so a control
        * that opens a second copy of it in a modal is a control for nothing.
        */}
      {choiceOffered ? (
        <div className={styles.viewControl}>
          {downloadButton}
          {choice}
        </div>
      ) : null}
      {listRoute === null ? null : (
        <button type="button" className={styles.close} onClick={() => navigate(listRoute)} aria-label={t('document.view.backToList')} title={t('document.view.backToList')} data-document-close={documentType}>
          {t('document.view.closeGlyph')}
        </button>
      )}
    </div>
  );
}

/**
 * 🔒 The document's own footer, built once for every kind (T168). The product
 * owner, 2026-09-22, on Edit above the sheet: *"Do not like the edit button new
 * placement at all"*. It sits on the floor of the page with the document's
 * outward moves, and Edit is the last thing in it, at the right.
 *
 * 🔨 The kind's own controls arrive as children and the attribute it has always
 * carried arrives with them, so what a suite reads the footer by does not move
 * when the bar is built here rather than nine times over.
 *
 * 🔨 There is no second Edit anywhere. The banner's pencil corrects a document in
 * place and is a different act (decision 0409); this one reopens the form.
 */
export function DocumentActionBar({ attributes, edit, leads = 2, children }: {
  attributes?: Record<string, string>;
  /** Edit, at the bar's right; absent where this status allows none. */
  edit?: ReactNode;
  /**
   * 🔒 How many acts stand on a phone before More (decision 3961, widening 3052's two). With three, the first takes
   * the footer's whole width and the other two share the row beneath it; the default leaves every document as it was.
   */
  leads?: number;
  children?: ReactNode;
}) {
  /*
   * 🔒 On a phone the bar is the page's first two acts and a full-width More holding the rest, in the order the
   * bar carries them (T2190; decision 3052, the owner's P2a). An act is a Button the bar draws, read off the drawn
   * bar so a kind's own component needs no list: the kind's moves, a reader's own act, Terms and Edit, in that
   * order, so Edit stands second beside a kind's one other act and is in More beside two or more. A footer of two
   * acts or fewer draws no More. From the tablet width up nothing here runs and the bar is the row it was.
   */
  const phone = usePhoneWidth();
  const bar = useRef<HTMLDivElement>(null);
  const [laid, setLaid] = useState({ acts: 0 });
  const [moreOpen, setMoreOpen] = useState(false);
  useLayoutEffect(() => {
    const element = bar.current;
    if (element === null) return undefined;
    const lay = () => {
      const next = layPhoneActs(element, phone, leads);
      setLaid((held) => (held.acts === next.acts ? held : next));
    };
    lay();
    if (!phone || typeof MutationObserver === 'undefined') return undefined;
    /* 🔨 A kind's own component redraws its buttons without the bar redrawing, so the bar lays them out again whenever its tree changes. */
    const observer = new MutationObserver(lay);
    observer.observe(element, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [phone, leads]);
  const withMore = phone && laid.acts > leads;
  /*
   * 🔒 Decision 4122 (T2405): More closes once an act in it is pressed, after the act's own handler has run, so the footer
   * returns to its first acts. 💰 It stayed open until More was pressed again, and a report whose Add Photo was taken from
   * More stood Add Photo, Book a Return Visit, Add Note and More full-width over half a phone. A destructive act keeps it
   * open, because its confirmation asks inside the act.
   */
  const closeAfterAct = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!withMore || !moreOpen) return;
    const act = event.target instanceof Element ? event.target.closest('[data-document-act]') : null;
    if (act === null || act.getAttribute('data-document-act') !== 'more' || act.getAttribute('data-color') === Color.Destructive) return;
    setMoreOpen(false);
  };
  return (
    <div
      ref={bar}
      className={styles.footerBar}
      onClick={closeAfterAct}
      {...(attributes ?? {})}
      {...(phone ? { 'data-document-actions-shape': 'phone', 'data-document-more': withMore && moreOpen ? 'open' : 'closed', 'data-document-leads': String(Math.min(laid.acts, leads)) } : {})}
    >
      <SplitAsButtonsContext.Provider value={withMore}>
        {children}
        {edit ? <span className={styles.footerBarRight}>{edit}</span> : null}
      </SplitAsButtonsContext.Provider>
      {withMore ? (
        <span data-document-more-toggle="true">
          <Button size={Size.Medium} color={Color.Inactive} text={[t('document.footer.more')]} expanded={moreOpen} actions={[() => setMoreOpen((open) => !open)]} />
        </span>
      ) : null}
    </div>
  );
}

/**
 * 🔒 Marks every element of a phone's footer with where it stands (decision 3052): `lead` for the first two acts,
 * `more` for the rest and for a split's own entries laid out apart, `wrap` for an element that only holds acts and
 * stands aside so they line up, and `note` for a figure, a hint or a refusal, which reads above the buttons. Off a
 * phone it clears every mark. A window drawn inside the footer is a note, and its own buttons are never acts.
 */
function layPhoneActs(bar: HTMLElement, phone: boolean, limit = 2): { acts: number } {
  for (const marked of Array.from(bar.querySelectorAll('[data-document-act]'))) {
    if (!phone) marked.removeAttribute('data-document-act');
    marked.removeAttribute('data-document-act-first');
  }
  if (!phone) return { acts: 0 };
  const acts: Element[] = [];
  const holdsAct = (element: Element) => Array.from(element.querySelectorAll('[data-split]')).some((button) => {
    const window = button.closest('dialog');
    return window === null || !element.contains(window);
  });
  const walk = (parent: Element) => {
    for (const child of Array.from(parent.children)) {
      if (child.hasAttribute('data-document-more-toggle')) continue;
      if (child.hasAttribute('data-split')) {
        acts.push(child);
      } else if (child.tagName === 'DIALOG' || !holdsAct(child)) {
        child.setAttribute('data-document-act', 'note');
      } else {
        child.setAttribute('data-document-act', 'wrap');
        walk(child);
      }
    }
  };
  walk(bar);
  let leads = 0;
  let own = 0;
  for (const act of acts) {
    const entry = act.hasAttribute('data-split-entry');
    if (!entry) own += 1;
    const place = !entry && leads < limit ? 'lead' : 'more';
    /* 🔒 Decision 3961: past the frame's two, the first act is marked so it takes the footer's whole width. */
    if (place === 'lead' && leads === 0 && limit > 2) act.setAttribute('data-document-act-first', 'true');
    if (place === 'lead') leads += 1;
    act.setAttribute('data-document-act', place);
  }
  return { acts: own };
}

/** 🔒 One party the Details view's parties block draws: its heading, its name and its address lines (decision 1120). */
export interface DocumentParty {
  /** What the suites read the column by: `recipient`, `deliverTo`, or a vendor's company id. */
  key: string;
  heading: string;
  name: string;
  lines: readonly string[];
  /** Drawn beside the name, such as an RFQ vendor's response pill. */
  aside?: ReactNode;
}

/** 🔨 Past this many parties the block is a row list, because a fourth column leaves an address two words wide (decision 1120). */
export const PARTY_COLUMNS_MAX = 3;

/**
 * 🔒 The parties block (T437, T442; decision 1120). Every party is a column under
 * its own heading, drawn in one style at one height, and the block carries no
 * heading of its own. 💰 The recipient's heading was the block's own heading and the
 * deliver-to's a grey label inside its column, so the two began at different heights.
 *
 * 🔨 One party draws at half width, the first of two tracks, never stretched. Past
 * three the parties are rows under one heading.
 */
export function DocumentParties({ documentType, parties, heading }: { documentType: string; parties: readonly DocumentParty[]; heading?: string }) {
  if (parties.length > PARTY_COLUMNS_MAX) {
    return (
      <section className={styles.block} data-document-parties={documentType} data-document-parties-shape="rows">
        <h2 className={styles.heading}>{heading ?? parties[0]?.heading ?? ''}</h2>
        <ul className={styles.partyRows}>
          {parties.map((party) => (
            <li key={party.key} className={styles.partyRow} data-document-party={party.key}>
              <span className={styles.rowName}>{party.name}</span>
              <span className={styles.rowNote}>{party.lines.join(', ')}</span>
              {party.aside ? <span className={styles.partyAside}>{party.aside}</span> : null}
            </li>
          ))}
        </ul>
      </section>
    );
  }
  /* 🔨 Never fewer than two tracks, so one party stands at half width. */
  const trackStyle = { '--document-party-columns': Math.max(parties.length, 2) } as CSSProperties;
  return (
    <section className={styles.block} data-document-parties={documentType} data-document-parties-shape="columns">
      <div className={styles.parties} style={trackStyle} data-document-party-columns={String(Math.max(parties.length, 2))}>
        {parties.map((party) => (
          <div key={party.key} className={styles.party} data-document-party={party.key}>
            <h2 className={styles.heading}>{party.heading}</h2>
            <p className={styles.rowName}>{party.name}</p>
            {party.lines.map((row, index) => (
              <p key={`${row}-${index}`} className={styles.rowNote}>{row}</p>
            ))}
            {party.aside ? <div className={styles.partyAside}>{party.aside}</div> : null}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * 🔒 The Internal Notes rail on the PDF view (T587; decision 1403). The product
 * owner, 2026-09-29: *"notes tile shows in PDF view too as a collapse Side bar on
 * right"*. It stands at the right of the sheet from the tablet width up.
 *
 * 🔒 On a phone it stands after the sheet in the page's flow, open, with its
 * tabs, and the sheet keeps the whole width above it (T2189; decision 3050,
 * amending 1456). The owner, 2026-10-10: *"Mobile view the Notes needs to just be
 * at the bottom of the page not hovering over like it is"*. 🔨 It draws no fold
 * there, and a phone neither reads nor writes the remembered fold.
 *
 * 🔒 It folds by the tile fold every notes and History tile already remembers
 * (decision 0500): per device, under the notes tile's own name, so folding it on
 * one invoice leaves it folded on the next. A browser that refuses to store opens
 * it, which is the safe way to be wrong.
 *
 * 🔨 Folded unmounts the notes, so a folded rail reads nothing; the chevron and
 * the title stay standing, because a rail that vanished would read as notes lost.
 */
/** The name the rail's fold is remembered under: the record page's Internal Notes tile's own title, never a translated heading. */
export const NOTES_FOLD_NAME = 'Internal Notes';

/** 🔒 The rail's tabs, in their order: Notes first, so a document opens on its notes as it always has (decision 1662). */
export const DOCUMENT_RAIL_TABS = ['notes', 'files', 'journal', 'history'] as const;
export type DocumentRailTab = (typeof DOCUMENT_RAIL_TABS)[number];

const RAIL_TAB_LABEL = {
  notes: 'document.rail.notes',
  files: 'document.rail.files',
  journal: 'document.rail.journal',
  history: 'document.rail.history',
} as const satisfies Readonly<Record<DocumentRailTab, StringKey>>;

/**
 * 🔒 The kinds whose rail chevron is the one chevron, so a tile handed in draws none of its own (decision 6683): the
 * Work Order, whose Notes tab is the record page's Internal Notes tile. Every other kind's panels keep their own
 * chevrons inside the rail (review of batch UX3).
 */
const RAIL_FOLDS_ITS_TILES: ReadonlySet<string> = new Set(['WorkOrder']);

/** Whether a kind handed a panel for a tab: a node, not nothing. */
function handed(node: ReactNode): boolean {
  return node !== undefined && node !== null && node !== false;
}

/**
 * 🔒 The rail is tabs (T1070; decision 1662, amending 1403): Notes, Files,
 * Journal and History, one open at a time, each the panel the Details view
 * draws. A tab stands only where the kind handed its panel, and with the notes
 * alone no tab row is drawn, which is the rail decision 1403 built. The rail
 * mounts only where the notes were handed, so Notes is always its first tab.
 *
 * 🔨 The tab chosen is not remembered: every document opens on its first tab,
 * Notes where the kind hands them. The fold is the rail's, remembered as before.
 */
export function DocumentNotesRail({ documentType, panels, place = 'side' }: {
  documentType: string;
  panels: Partial<Record<DocumentRailTab, ReactNode>>;
  /**
   * 🔒 Where the rail stands: at the sheet's right from the tablet width up, folding as decision 1403 built it, or
   * after the sheet in the page's flow on a phone, open and with no fold (T2189; decision 3050).
   */
  place?: 'side' | 'flow';
}) {
  const present = DOCUMENT_RAIL_TABS.filter((tab) => handed(panels[tab]));
  const [chosen, setChosen] = useState<DocumentRailTab | null>(null);
  const open: DocumentRailTab = chosen !== null && present.includes(chosen) ? chosen : present[0] ?? 'notes';
  /* 🔒 The strip names the panel that will open; on the notes it is the notes tile's own title, as it always was. */
  const name = open === 'notes' ? t('documentNotes.title') : t(RAIL_TAB_LABEL[open]);
  const flow = place === 'flow';
  const [remembered, setRemembered] = useState(() => tileFolded(NOTES_FOLD_NAME));
  /** 🔒 In the page's flow the panel is open on every document and the device's fold is neither read nor written (decision 3050). */
  const folded = flow ? false : remembered;
  const fold = (next: boolean) => {
    setRemembered(next);
    rememberTileFolded(NOTES_FOLD_NAME, next);
  };
  return (
    <aside className={styles.notesRail} aria-label={name} data-document-notes-rail={folded ? 'folded' : 'open'} data-document-notes-place={place} data-document-rail-tab={open} data-document-rail-tabs={present.join(' ')} data-document-rail-fold={RAIL_FOLDS_ITS_TILES.has(documentType) ? 'rail' : 'tiles'}>
      {flow ? null : <button
        type="button"
        className={styles.foldToggle}
        onClick={() => fold(!folded)}
        aria-expanded={!folded}
        aria-label={t(folded ? 'tile.expand' : 'tile.collapse')}
        title={t(folded ? 'tile.expand' : 'tile.collapse')}
        data-document-notes-rail-toggle={folded ? 'expand' : 'collapse'}
      >
        <Icon meaning={folded ? 'expand' : 'collapse'} size="inline" />
      </button>}
      {folded ? <span className={styles.notesRailLabel}>{name}</span> : (
        <>
          {present.length > 1 ? (
            <div className={railStyles.tabs} data-document-rail-tab-row={documentType}>
              <SegmentedTab
                name={`document-rail-${documentType}`}
                legend={t('document.rail.legend')}
                segments={present.map((tab) => ({ value: tab, label: t(RAIL_TAB_LABEL[tab]) }))}
                value={open}
                onChange={(next) => setChosen(present.find((tab) => tab === next) ?? null)}
              />
            </div>
          ) : null}
          <div className={`${styles.notesRailBody} ${railStyles.panel}`} data-document-rail-panel={open}>{panels[open]}</div>
        </>
      )}
    </aside>
  );
}

/**
 * 🔒 The sheet's warning as one banner (decision 1401; T591): it opens once, draws
 * each rejected region as its own line, and gives the fix once at the end, so a
 * template with several bad regions does not repeat the opening and the fix.
 */
function SheetWarningMessage({ lines }: { lines: readonly string[] }) {
  const opening = lines.length === 1 ? t('document.view.sheetWarningOpeningOne') : fill(t('document.view.sheetWarningOpeningMany'), { count: String(lines.length) });
  return (
    <>
      <span className={styles.sheetWarningLine}>{opening}</span>
      {lines.map((line, index) => (
        <span key={index} className={styles.sheetWarningLine} data-document-sheet-warning-line={index + 1}>
          {line}
        </span>
      ))}
      <span className={styles.sheetWarningLine}>{t('document.view.sheetWarningFix')}</span>
    </>
  );
}

export function DocumentViewPage({
  documentType,
  icon,
  title,
  subtitle,
  information,
  status,
  kinds,
  controls,
  toolbar = null,
  gear = null,
  tabs,
  footer,
  footerAttributes,
  footerLeads = 2,
  onEdit,
  editAction,
  onCorrect,
  pageAttributes,
  notices,
  whatsNext = null,
  print = null,
  parties: kindParties = null,
  partiesHeading,
  sheet: sheetAnswer = null,
  layout = null,
  ribbon = null,
  ribbonStatus = null,
  ribbonGroup: ribbonGroupGiven = null,
  pdfPath = null,
  sheetOffered = true,
  sheetHeader = null,
  listRoute = null,
  lines = null,
  details,
  notes = null,
  files = null,
  journal = null,
  history = null,
  children,
  apiBaseUrl = '',
  terms = null,
  writeRefusal = null,
  readerAction = null,
}: DocumentViewPageProps) {
  /**
   * 🔨 A kind whose answer carries no projection opens on Details rather than on
   * an empty sheet (found while building, 2026-09-21). Its rendered sheet is
   * still one press away, because the server renders one.
   *
   * 🔒 The opening view is DERIVED and not initialised once (found in review,
   * 2026-09-21). A page whose sheet arrives after its first render — a second read,
   * a slow answer — held the view it chose while the sheet was absent, so it opened
   * on Details for good and no kind's own fix could reach it. What a person presses
   * is remembered here and wins; with nothing pressed, the sheet's presence decides,
   * on every render.
   */
  /*
   * 🔒 A sheet whose stored template has a region outside the geometry rule still
   * arrives drawn, with one sentence for each such region naming the template and
   * the region (decision 1401, amending 1340). The lines stand above both views
   * as one warning banner that opens once and gives the fix once (T591), and the
   * sheet opens as any other sheet does.
   */
  const sheetWarnings = sheetAnswer?.warnings ?? [];
  const navigate = useNavigate();
  const resolveStatus = useStatusResolver();
  /**
   * 🔒 The band takes the status's own colour group and its own label's words, read off the one status vocabulary by
   * the stored value (decision 1457); a value it cannot place reads grey, in the words the page passed. 💰 The value
   * drawn in capitals ran its words together, so an invoice read PARTIALLYPAID (UA3 fix 2).
   */
  const ribbonRow = ribbon && !ribbonGroupGiven ? resolveStatus(`${documentType}Status`, ribbonStatus ?? ribbon) : null;
  const ribbonGroup = ribbon ? ribbonGroupGiven ?? ribbonRow?.colorGroup ?? 'inactive' : null;
  const ribbonWords = ribbon ? (ribbonRow?.label ?? ribbon).toUpperCase() : null;
  const sheetHtml = sheetAnswer;
  const [chosen, setChosen] = useState<DocumentView | null>(null);
  const [termsOpen, setTermsOpen] = useState(false);
  /** 🔒 Below the tablet width the PDF view scrolls the page and the notes follow the sheet (T2189; decision 3050). */
  const phone = usePhoneWidth();
  /**
   * 🔒 Below 1280 with the list pane open beside it, the notes rail stands under the sheet as on a phone, so the sheet
   * takes the document's whole column; the list stays open (T3685; decision 11540). 💰 Beside the list and the rail the
   * sheet's column measured 58 pixels at 1024 and 102 at 768 (red landing 41139296157).
   */
  const listOpen = useListPaneOpen();
  const narrow = useBelowWidth(componentBreakpoints.documentRailBeside);
  const flowed = phone || (listOpen && narrow);
  /** 🔒 Decision 4002: a link that names the Details view opens on it, once, and the remembered choice stands. */
  const [params] = useSearchParams();
  const linked: DocumentView | null = params.get(DOCUMENT_VIEW_PARAM) === 'details' ? 'details' : null;
  const view: DocumentView = chosen ?? linked ?? (sheetHtml === null ? 'details' : readDocumentView());
  const choose = (next: DocumentView) => {
    setChosen(next);
    rememberDocumentView(next);
  };

  /*
   * 🔒 The two bars above both views (decision 0612), drawn by the one component
   * so the Work Order — which is a record page with tiles, not a page of its own
   * — carries the same choice, the same sheet control and the same way out.
   */
  const onSheet = view === 'pdf' && sheetHtml !== null;
  /*
   * 🔒 The PDF view's tools are one quiet row at every width (decision 6703 extends 3051 past the phone; T2971):
   * the switch at its head, the zoom as − and +, Fit Page, the page count, and the download at its right; the
   * cross is not drawn, because the switch is how a person leaves the sheet. The Details view's bar is unchanged.
   */
  const phoneParts = { documentType, documentName: title, view, onChoose: choose, pdfPath, apiBaseUrl, listRoute, ...(tabs === undefined ? {} : { tabs }) };
  const bars = (
    <DocumentViewControls
      documentType={documentType}
      documentName={title}
      view={view}
      onChoose={choose}
      pdfPath={pdfPath}
      apiBaseUrl={apiBaseUrl}
      listRoute={listRoute}
      choiceOffered={sheetOffered}
      {...(tabs === undefined ? {} : { tabs })}
    />
  );

  /**
   * 🔒 The page's own footer, on the floor, with Edit at its right (T168). It is
   * always rendered, because every accounting document view carries the bar even
   * where this status offers no move — which is why the shell's footer is shown on
   * footer content rather than on the presence of a status pill (T113).
   */
  const refused = typeof writeRefusal === 'string' && writeRefusal !== '';
  const editControl = refused ? null : editAction ?? (onEdit ? (
    <span data-document-edit={documentType}>
      <Button size={Size.Medium} color={Color.Navy} text={[t('document.edit.action')]} actions={[onEdit]} />
    </span>
  ) : null);
  const footerBar = (
    <DocumentActionBar
      attributes={{ 'data-document-actions': documentType, ...(footerAttributes ?? {}) }}
      leads={footerLeads}
      {...(editControl === null ? {} : { edit: editControl })}
    >
      {/* 🔒 A viewer the document write gate refuses is told why, and offered none of the moves (decision 0687). */}
      {refused ? <p className={styles.muted} data-document-write-refused={documentType}>{writeRefusal}</p> : footer}
      {readerAction}
      {/* 🔒 Terms, on each of the seven printed kinds (decision 0684); it reads where Edit is not offered and says why. */}
      {terms !== null && isTermPrintedKind(documentType) ? (
        <span data-document-terms-action={documentType}>
          <Button size={Size.Medium} text={[t('document.terms.action')]} actions={[() => setTermsOpen(true)]} />
        </span>
      ) : null}
    </DocumentActionBar>
  );

  const sheet = (
    <>
      {sheetHtml === null ? (
        /* 🔨 A kind whose answer carries no sheet says so rather than drawing an empty frame. */
        <p className={styles.muted} data-document-no-sheet={documentType}>{t('document.view.noSheet')}</p>
      ) : (
        <div className={styles.sheetWrap}>
          {/*
            🔒 The band reads the status in capitals across the sheet's top-left corner, on screen only: it is not in the HTML the printer is given, so it cannot reach the paper (decisions 0612, 1457).
            🔨 It rides the frame's own corner layer, which scales with the paper and clips to it, so it sits on the sheet at every zoom step.
          */}
          <DocumentSheetFrame
            sheet={sheetHtml}
            layout={layout}
            title={t('document.sheet.frame')}
            corner={ribbon ? <span className={styles.ribbon} data-document-ribbon={ribbon} data-document-ribbon-group={ribbonGroup ?? 'inactive'}>{ribbonWords}</span> : null}
            controls={<DocumentViewControls {...phoneParts} part="download" />}
            lead={<DocumentViewControls {...phoneParts} part="switch" />}
            compact
            flow={flowed}
          />
        </div>
      )}
    </>
  );

  const partyList: readonly DocumentParty[] | null = kindParties ?? (print === null ? null : [
    { key: 'recipient', ...print.recipient },
    ...(print.deliverTo ? [{ key: 'deliverTo', ...print.deliverTo }] : []),
  ]);
  /* 🔨 A kind naming no party yet, such as an RFQ asked of nobody, draws no empty block. */
  const parties = partyList === null || partyList.length === 0 ? null : (
    <DocumentParties documentType={documentType} parties={partyList} {...(partiesHeading === undefined ? {} : { heading: partiesHeading })} />
  );

  return (
    <PageModule
      icon={icon}
      avatar={<BannerAvatarSlot glyph={icon} title={title} />}
      title={title}
      subtitle={subtitle ?? null}
      {...(controls === undefined ? {} : { controls, controlsInline: true })}
      toolbar={toolbar}
      {...(gear === null ? {} : { gear })}
      {...(kinds === undefined ? {} : { kinds })}
      {...(status === undefined ? {} : { status })}
      {...(onCorrect === undefined || refused ? {} : { onEdit: onCorrect })}
      footer={footerBar}
    >
      {/*
        🔒 One scroller (T205; decision 0694): on the PDF view the page is the content
        region's own height and the sheet's pane scrolls inside it, so the page body
        does not scroll beside it. The Details view scrolls the page as every page does.
      */}
      {/* 🔒 On a phone, and below 1280 beside the open list, the page is the one scroller, so the notes can follow the sheet (T2189, T3685; decisions 3050, 11540). */}
      <div className={styles.page} data-document-scroll={onSheet && !flowed ? 'pane' : 'page'} {...(pageAttributes ?? {})}>
        {notices}
        {refused ? null : whatsNext}
        {/* 🔒 Decision 1401: the warning names the template and the region; its one action opens the page the fix is made on, since a warning offers an action (piece 3). */}
        {sheetWarnings.length === 0 ? null : (
          <div data-document-sheet-warning={documentType}>
            <AlertBanner tone="warning" message={<SheetWarningMessage lines={sheetWarnings} />} action={{ label: t('document.view.sheetWarningAction'), onAction: () => navigate(PRINT_TEMPLATES_ROUTE) }} />
          </div>
        )}
        {/* 🔒 On the PDF view the bar stands on the zoom row, one control row above the sheet (T335; decision 0626); on Details it stands here, above the facts. */}
        {onSheet ? null : bars}
        {/* 🔒 The kind's own control above the sheet, on the PDF view alone (decision 1513). */}
        {view === 'pdf' && sheetHeader !== null ? <div data-document-sheet-header={documentType}>{sheetHeader}</div> : null}

        {/* 🔒 The notes, the files, the journal and the history stand beside the sheet on the PDF view as the rail's tabs, so a person reads the paper and what surrounds it together (T587, T1070; decisions 1403, 1662). */}
        {/* 🔒 A kind whose page read withheld its notes, as it does to an outside login, mounts no rail at any width, its other tabs with it (found in review, 2026-09-30; decision 1662). */}
        {view === 'pdf' ? (!handed(notes) ? sheet : (
          <div className={styles.sheetWithRail} data-document-notes-layout={documentType} data-document-notes-place={flowed ? 'flow' : 'side'}>
            {sheet}
            <DocumentNotesRail key={terms?.documentId ?? pdfPath ?? documentType} documentType={documentType} panels={{ notes, files, journal, history }} place={flowed ? 'flow' : 'side'} />
          </div>
        )) : (
          <>
            {/* 🔒 What the document is against, at the top of the Details view (T170). */}
            <PageFacts facts={information ?? []} />
            {parties}
            {lines === null ? null : (
              <section className={styles.block} {...(lines.blockAttribute ? { [lines.blockAttribute]: String(lines.rows.length) } : {})}>
                <h2 className={styles.heading}>{lines.title}</h2>
                {lines.hint ? <p className={styles.muted} data-lines-hint="true">{lines.hint}</p> : null}
                <DocumentLines lines={lines} />
              </section>
            )}
            {details}
          </>
        )}
        {children}
        {/* 🔨 Mounted only while open, so each opening reads the document again (the modal-initial trap). */}
        {termsOpen && terms !== null ? (
          <DocumentTermsModal
            documentType={documentType}
            documentId={terms.documentId}
            apiBaseUrl={apiBaseUrl}
            onCancel={() => setTermsOpen(false)}
            onSaved={() => { setTermsOpen(false); terms.onSaved(); }}
          />
        ) : null}
      </div>
    </PageModule>
  );
}
